import React, { useEffect, useId } from "react";

import {
  FilterName,
  Filters,
  Groups,
  useDashboardEngagement,
  Quarter,
} from "apps/company/api/rest/company-dashboard-engagement";
import { useTranslation } from "lib/i18n/use-translation";

import Dropdown from "../Dropdown";
import engagementStyles from "../engagement/engagement.module.scss";
import { DashboardFilter, useDashboardTracking } from "../tracking";
import styles from "./engagement-redesign.module.scss";
import RedesignFigures from "./RedesignFigures";

const K = "admin:companyDashboard.engagement";

const QUARTER_VALUE = /^(\d{4})-Q([1-4])$/;

// Platform's `label` is English ("Q4 2026"); each locale writes a quarter its
// own way, so the page builds it from the `value` instead.
const useQuarterLabel = () => {
  const { t } = useTranslation(["admin"]);
  return (value: string) => {
    const match = QUARTER_VALUE.exec(value);
    if (match === null) throw new Error(`Not a quarter: ${value}`);
    const [, year, number] = match;
    return t(`${K}.quarter.name`, { year, number });
  };
};

// The UTC day the figures run to, written the viewer's way.
const useDay = () => {
  const { i18n } = useTranslation(["admin"]);
  return (day: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(`${day}T00:00:00Z`));
};

const QuarterControl = ({
  quarters,
  selected,
  onSelect,
}: {
  quarters: Quarter[];
  selected: string | null;
  onSelect: (quarter: string) => void;
}) => {
  const { t } = useTranslation(["admin"]);
  const quarterLabel = useQuarterLabel();
  const id = useId();
  if (quarters.length === 0) return null;
  return (
    <div className={styles["quarter"]}>
      <span id={id} className={styles["visually-hidden"]}>
        {t(`${K}.quarter.control`)}
      </span>
      <Dropdown
        labelId={id}
        value={selected ?? quarters[0].value}
        onChange={onSelect}
        options={quarters.map((quarter) => ({
          value: quarter.value,
          label: quarterLabel(quarter.value),
        }))}
      />
    </div>
  );
};

// Each group filter as the prototype names it, and as Mixpanel hears of it.
const FILTERS: { name: FilterName; tracked: DashboardFilter }[] = [
  { name: "location", tracked: "geography" },
  { name: "business_unit", tracked: "department" },
  { name: "manager", tracked: "people" },
];

// The value the Dropdown holds for "all": never a group name, which Platform
// never sends empty.
const ALL = "";

const useFilterOptions = () => {
  const { t } = useTranslation(["admin"]);
  return (name: FilterName, offered: string[], chosen: string | null) => {
    // A group chosen for another quarter stays listed, so the control never
    // claims "all" while the figures are for one group.
    const values =
      chosen !== null && !offered.includes(chosen)
        ? [...offered, chosen]
        : offered;
    const label = (value: string) => {
      if (name !== "manager") return value;
      return value === "true"
        ? t(`${K}.filters.managers`)
        : t(`${K}.filters.nonManagers`);
    };
    return [
      { value: ALL, label: t(`${K}.filters.all.${name}`) },
      ...values.map((value) => ({ value, label: label(value) })),
    ];
  };
};

const FilterControls = ({
  groups,
  filters,
  onFilter,
  onClear,
}: {
  groups: Groups;
  filters: Filters;
  onFilter: (name: FilterName, value: string | null) => void;
  onClear: () => void;
}) => {
  const { t } = useTranslation(["admin"]);
  const options = useFilterOptions();
  const id = useId();
  const shown = FILTERS.filter(
    ({ name }) => groups[name].length > 0 || filters[name] !== null
  );
  if (shown.length === 0) return null;
  // With one control there is nothing to combine, so nothing to explain.
  const noted = shown.length > 1;
  const anyChosen = FILTERS.some(({ name }) => filters[name] !== null);
  return (
    <div className={styles["filter-bar"]}>
      <div className={styles["filters"]}>
        {shown.map(({ name }) => (
          <React.Fragment key={name}>
            <span id={`${id}-${name}`} className={styles["visually-hidden"]}>
              {t(`${K}.filters.control.${name}`)}
            </span>
            <Dropdown
              labelId={`${id}-${name}`}
              describedBy={noted ? `${id}-note` : undefined}
              value={filters[name] ?? ALL}
              onChange={(value) => onFilter(name, value === ALL ? null : value)}
              options={options(name, groups[name], filters[name])}
              sizeTo={
                name === "manager"
                  ? [
                      t(`${K}.filters.all.manager`),
                      t(`${K}.filters.managers`),
                      t(`${K}.filters.nonManagers`),
                    ]
                  : t(`${K}.filters.all.${name}`)
              }
            />
          </React.Fragment>
        ))}
        <button
          type="button"
          className={styles["clear"]}
          data-hidden={!anyChosen}
          aria-hidden={!anyChosen}
          tabIndex={anyChosen ? undefined : -1}
          onClick={() => {
            // Clear is hidden once the groups are cleared, and a hidden button
            // loses focus to the page, so focus moves to the first filter.
            const first = FILTERS.find(({ name }) => groups[name].length > 0);
            if (first) {
              document
                .querySelector<HTMLButtonElement>(
                  `[aria-labelledby="${id}-${first.name}"][aria-haspopup]`
                )
                ?.focus();
            }
            onClear();
          }}
        >
          {t(`${K}.filters.clear`)}
        </button>
      </div>
      {noted && (
        <p id={`${id}-note`} className={styles["filter-note"]}>
          {t(`${K}.filters.note`)}
        </p>
      )}
    </div>
  );
};

// The Engagement page behind `client_dashboard_engagement`: the figures for a
// chosen quarter, cumulative as of its end, for the whole company or for one
// office, one department, or managers or everyone else.
const EngagementRedesign = () => {
  const { t } = useTranslation(["admin"]);
  const day = useDay();
  const quarterLabel = useQuarterLabel();
  const {
    status,
    quarters,
    selected,
    filters,
    groups,
    shown,
    companyName,
    open,
    select,
    filter,
    clearFilters,
    retry,
  } = useDashboardEngagement();
  const { filterChanged } = useDashboardTracking();
  const selectQuarter = (quarter: string) => {
    filterChanged("engagement", "quarter");
    select(quarter);
  };
  const chooseGroup = (name: FilterName, value: string | null) => {
    filterChanged("engagement", FILTERS.find((f) => f.name === name)!.tracked);
    filter(name, value);
  };
  const clearGroups = () => {
    FILTERS.filter(({ name }) => filters[name] !== null).forEach(
      ({ tracked }) => filterChanged("engagement", tracked)
    );
    clearFilters();
  };

  useEffect(() => {
    open();
  }, [open]);

  return (
    <div className={engagementStyles["engagement"]}>
      <header className={styles["header"]}>
        <div className={styles["header-text"]}>
          <h2 className={engagementStyles["heading"]}>{t(`${K}.tab`)}</h2>
          {companyName !== null && (
            <p className={engagementStyles["subheading"]}>
              {t(`${K}.subtitle`, { company: companyName })}
            </p>
          )}
        </div>
        <div className={styles["header-side"]}>
          {groups !== null && (
            <FilterControls
              groups={groups}
              filters={filters}
              onFilter={chooseGroup}
              onClear={clearGroups}
            />
          )}
          <QuarterControl
            quarters={quarters}
            selected={selected}
            onSelect={selectQuarter}
          />
        </div>
      </header>

      {(status.state === "idle" || status.state === "loading") && (
        <div className={engagementStyles["status"]} role="status">
          <h3 className={engagementStyles["card-title"]}>
            {t(`${K}.quarter.loading.title`)}
          </h3>
          <p className={engagementStyles["card-description"]}>
            {t(`${K}.quarter.loading.body`)}
          </p>
        </div>
      )}

      {status.state === "error" && (
        <div className={engagementStyles["status"]} role="alert">
          <p className={engagementStyles["card-description"]}>
            {t(`${K}.quarter.error`)}
          </p>
          <button
            type="button"
            className={styles["retry"]}
            onClick={() => retry()}
          >
            {t(`${K}.quarter.retry`)}
          </button>
        </div>
      )}

      {status.state === "ready" && (
        <RedesignFigures
          tiles={status.report.tiles}
          comments={status.report.comments}
          teams={status.report.teams}
          // The filters asked for, which every answer's figures are for: an
          // answer from an older Platform may not name them.
          filtered={FILTERS.some(({ name }) => filters[name] !== null)}
          day={day(status.report.quarter.data_through)}
          quarter={quarterLabel(status.report.quarter.value)}
        />
      )}

      {/* Once the figures are in, the funnel card says this instead. */}
      {shown !== null && status.state !== "ready" && (
        <p className={styles["footer"]}>
          {t(`${K}.quarter.footer`, { date: day(shown.data_through) })}
        </p>
      )}
    </div>
  );
};

export default EngagementRedesign;
